<script setup lang="ts">
import { computed, ref } from 'vue';
import { ComparisonResult, YearResult } from '../lib/types';
import { formatCurrency } from '../lib/math';

const props = defineProps<{
  result: ComparisonResult;
}>();

const expanded = ref(false);

const rows = computed<YearResult[]>(() => {
  const maxYears = Math.max(props.result.jdg.yearly.length, props.result.spzoo.yearly.length);
  const merged: YearResult[] = [];
  for (let i = 0; i < maxYears; i++) {
    const jdgRow = props.result.jdg.yearly[i];
    const spRow = props.result.spzoo.yearly[i];
    merged.push({
      year: i + 1,
      jdgContribution: jdgRow?.jdgContribution ?? 0,
      spContribution: spRow?.spContribution ?? 0,
      jdgPortfolioEnd: jdgRow?.jdgPortfolioEnd ?? 0,
      spPortfolioEnd: spRow?.spPortfolioEnd ?? 0,
      jdgTaxes: jdgRow?.jdgTaxes ?? 0,
      spTaxes: spRow?.spTaxes ?? 0,
      salaryGross: spRow?.salaryGross ?? 0,
      dividendsGross: spRow?.dividendsGross ?? 0,
      loanWithdrawals: spRow?.loanWithdrawals ?? 0,
      interestPaid: spRow?.interestPaid ?? 0,
      ownerCashReceived: (jdgRow?.ownerCashReceived ?? 0) + (spRow?.ownerCashReceived ?? 0)
    });
  }
  return merged;
});
</script>

<template>
  <div class="card">
    <div class="section-title" @click="expanded = !expanded">
      <span>Year-by-year breakdown</span>
      <span class="badge">{{ expanded ? 'Hide' : 'Show' }}</span>
    </div>
    <div v-if="expanded" style="overflow-x: auto; margin-top: 0.75rem">
      <table>
        <thead>
          <tr>
            <th>Year</th>
            <th>JDG invest</th>
            <th>Sp. z o.o. invest</th>
            <th>JDG taxes</th>
            <th>Sp. z o.o. taxes</th>
            <th>Salary gross</th>
            <th>Dividends</th>
            <th>Loan draws</th>
            <th>Interest paid</th>
            <th>Owner cash (consumption)</th>
            <th>JDG portfolio</th>
            <th>Sp. z o.o. portfolio</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.year">
            <td>{{ row.year }}</td>
            <td>{{ formatCurrency(row.jdgContribution) }}</td>
            <td>{{ formatCurrency(row.spContribution) }}</td>
            <td>{{ formatCurrency(row.jdgTaxes) }}</td>
            <td>{{ formatCurrency(row.spTaxes) }}</td>
            <td>{{ formatCurrency(row.salaryGross) }}</td>
            <td>{{ formatCurrency(row.dividendsGross) }}</td>
            <td>{{ formatCurrency(row.loanWithdrawals) }}</td>
            <td>{{ formatCurrency(row.interestPaid) }}</td>
            <td>{{ formatCurrency(row.ownerCashReceived) }}</td>
            <td>{{ formatCurrency(row.jdgPortfolioEnd) }}</td>
            <td>{{ formatCurrency(row.spPortfolioEnd) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
